import React, { Fragment, RefObject, useRef, useState } from "react";
import moment from "moment";

import { useDeleteReport, useGetAssessments, useUploadReport } from "../../hooks/assessments";

import { SVGIcon, SVGIconColor } from "../wrappers/SVGIcon";

import { IAssessmentListItemForAdmin } from "../../interfaces/services";

import { Icons } from "../../data/Assets";

interface IAssessmentsProps {
    organizationId: number;
};
export const Assessments = (props: IAssessmentsProps) => {
    const { organizationId } = props;

    const { data: assessments, isLoading } = useGetAssessments(organizationId);

    return (
        <div className="immployer_admin__organization_assessments">
            {isLoading &&
                <div className="immployer_admin__organization_assessments__loading">
                    <span className="immployer_admin__btn_loader" />
                    <span>Loading Assessments</span>
                </div>
            }
            {!isLoading && !assessments?.length &&
                <div className="immployer_admin__organization_assessments__no_assessments">
                    No assessments found
                </div>
            }
            {!isLoading && assessments?.length &&
                <div className="immployer_admin__organization_assessments__list">
                    {assessments.map(assessment =>
                        <AssessmentAccordion
                            key={assessment.id}
                            assessment={assessment}
                        />
                    )}
                </div>
            }
        </div>
    );
};

interface IAssessmentAccordionProps {
    assessment: IAssessmentListItemForAdmin;
};
const AssessmentAccordion = (props: IAssessmentAccordionProps) => {
    const { assessment } = props;

    const fileRef = useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [isReportUploading, setIsReportUploading] = useState(false);
    const [isReportDeleting, setIsReportDeleting] = useState(false);

    const { mutate: deleteReport } = useDeleteReport();
    const { mutate: uploadReport } = useUploadReport();

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const handleDeleteReport = () => {
        if (isReportDeleting || isReportUploading) return;

        deleteReport({
            id: assessment.id,
            setIsReportDeleting: setIsReportDeleting,
        });
    };

    const onOpenFile = (fileRef: RefObject<HTMLInputElement>) => {
        if (isReportDeleting || isReportUploading) return;

        if (fileRef.current) {
            fileRef.current.click();
        };
    };

    const onFileChange = (fileRef: RefObject<HTMLInputElement>) => {
        if (isReportDeleting || isReportUploading) return;

        if (fileRef.current?.files) {
            const file = fileRef.current.files[0];

            fileRef.current.value = '';

            uploadReport({
                id: assessment.id,
                file,
                setIsReportUploading,
            });
        };
    };

    const getForeignWorkerName = () => {
        if (assessment.foreign_worker) {
            return `${assessment.foreign_worker.worker_details[0].first_name} ${assessment.foreign_worker.worker_details[0].last_name ?? ''}`;
        } else if (assessment.worker_name) {
            return assessment.worker_name;
        }

        return "N/A";
    };

    const getExperienceRequired = () => {
        const experiences = [
            { value: '0', label: "No experience" },
            { value: '0-2', label: "0 to 2 years" },
            { value: '2-5', label: "2 to 5 years" },
            { value: '5-10', label: "5 to 10 years" },
            { value: '10+', label: "10 years +" },
        ];

        const experience = experiences.find(exp => exp.value === assessment.experience);

        return experience?.label ?? "N/A";
    };

    return (
        <div className={`immployer_admin__organization_assessment__accordion ${assessment.report_url ? "immployer_admin__organization_assessment__accordion--report_uploaded" : ""}`}>
            <button
                className="immployer_admin__organization_assessment__header"
                onClick={toggleAccordion}
            >
                <div className="immployer_admin__organization_assessment__header__left">
                    <h5>{assessment.title.length > 50 ? assessment.title.slice(0, 50) + "..." : assessment.title}</h5>
                </div>
                <div className="immployer_admin__organization_assessment__header__right">
                    {!assessment.report_url && assessment.questionnaire_status === 'finished' &&
                        <div className="immployer_admin__organization_assessment__header__right__status">
                            <SVGIcon src={Icons.CheckCircleFill} color="success" />
                            <span>Questionnaire Complete</span>
                        </div>
                    }
                    {assessment.report_url &&
                        <div className="immployer_admin__organization_assessment__header__right__status">
                            <SVGIcon src={Icons.CheckCircleFill} color="success" />
                            <span>Report Uploaded</span>
                        </div>
                    }
                    <SVGIcon src={Icons.ChevronSharp} rotation={isOpen ? 180 : 0} color="black" />
                </div>
            </button>
            {isOpen &&
                <div className="immployer_admin__organization_assessment__content">
                    <div className="immployer_admin__organization_assessment__content__grp">
                        <br />
                        <h5>Assessment Report:</h5>
                        <div className="immployer_admin__organization_assessment__content__report_container">
                            {assessment.report_url
                                ? <Fragment>
                                    <a href={assessment.report_url ?? ''} target="_blank" rel="noopener noreferrer">View Report</a>
                                    <button className="immployer_admin__delete_report_btn" onClick={handleDeleteReport}>
                                        {isReportDeleting
                                            ? <i className='immployer_admin__btn_loader' />
                                            : <SVGIcon src={Icons.Trash2Fill} color="error" />
                                        }
                                    </button>
                                </Fragment>
                                : <>
                                    <input className='immployer_admin__file_upload_sudo_input' ref={fileRef} type='file' accept='application/pdf' onChange={() => onFileChange(fileRef)} />
                                    <button className="immployer_admin__btn immployer_admin__btn__tertiary" onClick={() => onOpenFile(fileRef)}>
                                        {isReportUploading
                                            ? <i className='immployer_admin__btn_loader' />
                                            : <SVGIcon src={Icons.Upload2} color="primary" />
                                        }
                                        <span>{isReportUploading ? "Uploading..." : "Upload Report"}</span>
                                    </button>
                                </>
                            }
                        </div>
                        <br /><br />
                        <h5>Overview:</h5>
                        <div className="immployer_admin__organization_assessment__content__grp">
                            <GroupedItem label="Requested on:" value={moment(assessment.created_at).format('MMM D, YYYY')} />
                            <GroupedItem label="Name:" value={getForeignWorkerName()} />
                            <GroupedItem
                                label="Invitation Status:"
                                value={assessment.latest_invitation_status ? assessment.latest_invitation_status[0].toUpperCase() + assessment.latest_invitation_status.slice(1) : assessment.invited_at ? "Invited" : "N/A"}
                                valueIcon={assessment.latest_invitation_status === 'pending' ? { src: Icons.DotsCircleFill, color: "primary" } : assessment.latest_invitation_status === 'accepted' ? { src: Icons.CheckCircleFill, color: "success" } : null}
                            />
                            <GroupedItem
                                label="Questionnaire Status:"
                                value={assessment.questionnaire_status ? assessment.questionnaire_status[0].toUpperCase() + assessment.questionnaire_status.slice(1) : "N/A"}
                                valueIcon={assessment.questionnaire_status === 'finished' ? { src: Icons.CheckCircleFill, color: "success" } : null}
                            />
                        </div>
                        <br />
                        <h5>Attachments:</h5>
                        <div className="immployer_admin__organization_assessment__content__grp">
                            <GroupedItem label="Resume:" value={!assessment.resume_url ? "N/A" : ''} valueUrl={assessment.resume_url ?? undefined} />
                            <GroupedItem label="Job Description:" value={!assessment.job_description_url ? "N/A" : ''} valueUrl={assessment.job_description_url ?? undefined} />
                        </div>
                        <br />
                        <h5>Assessment Details:</h5>
                        <div className="immployer_admin__organization_assessment__content__grp">
                            <GroupedItem label="Job Title:" value={`${assessment.noc} - ${assessment.title}`} />
                            <GroupedItem label="Employer:" value={assessment.location.employer.legal_name} />
                            <GroupedItem label="Location:" value={`${assessment.location.street}, ${assessment.location.city}, ${assessment.location.province}, ${assessment.location.postal}.`} />
                            <GroupedItem label="Experience required:" value={getExperienceRequired()} />
                            <GroupedItem label="Role:" value={assessment.full_time ? "Full-Time" : "Part-Time"} />
                            <GroupedItem label="Wage:" value={`$${assessment.wage_min} - $${assessment.wage_max} ${assessment.wage_type === "hourly" ? "/hour" : "/year"}`} />
                            <GroupedItem label="Unionized:" value={assessment.unionized ? "Yes" : "No"} />
                            <GroupedItem label="Specialized:" value={assessment.specialized ? "Yes" : "No"} />
                            <GroupedItem label="IP Related:" value={assessment.ip_related ? "Yes" : "No"} />
                            <GroupedItem label="Overseas Location:" value={assessment.overseas_location ? "Yes" : "No"} />
                            <GroupedItem label="Other Country:" value={assessment.other_country ? "Yes" : "No"} />
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

interface IGroupedItemProps {
    label: string;
    value: string;
    valueIcon?: { src: string, color: SVGIconColor } | null;
    valueUrl?: string;
};
const GroupedItem = (props: IGroupedItemProps) => {
    const { label, value, valueIcon, valueUrl } = props;

    return (
        <div className="immployer_admin__organization_assessment__content__grp__item">
            <p className="immployer_admin__organization_assessment__content__grp__item__label">{label}</p>
            <p className="immployer_admin__organization_assessment__content__grp__item__value">{value}</p>
            {valueIcon &&
                <div className="immployer_admin__organization_assessment__content__grp__item__value__icon">
                    <SVGIcon src={valueIcon.src} color={valueIcon.color} />
                </div>
            }
            {valueUrl && <a href={valueUrl} target="_blank" rel="noopener noreferrer">View</a>}
        </div>
    );
};